import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Chip,
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_ALL_MANAGERS } from './notification-managers.gql';

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '12px',
      backgroundColor: '#fff',
    },
    searchField: {
      marginBottom: '12px',
    },
    selectedManagersContainer: {
      marginBottom: '12px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      minHeight: '32px',
    },
    managerChip: {
      margin: '2px',
    },
    managersList: {
      maxHeight: '200px',
      overflowY: 'auto',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
    },
    listItem: {
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    avatar: {
      width: 32,
      height: 32,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: '20px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '20px',
      color: '#666',
    },
    managerName: {
      fontSize: '14px',
    },
    managerEmail: {
      fontSize: '12px',
      color: '#666',
    },
  })
);

interface Manager {
  managerId: string;
  profile: {
    profileId: string;
    userId: string;
    preferredName: string;
    lastName: string;
    email: string;
    avatarUrl?: string;
  };
}

interface GetAllManagers {
  vm: {
    admins: Manager[];
  };
}

interface ManagerSelectorProps {
  selectedManagerIds: string[];
  onChange: (managerIds: string[]) => void;
  error?: string;
  disabled?: boolean;
}

const ManagerSelector: React.FC<ManagerSelectorProps> = ({ selectedManagerIds, onChange, error, disabled = false }) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');

  const { data, loading, error: queryError } = useCampfireQuery<GetAllManagers, {}>(GET_ALL_MANAGERS, {});

  const managers = data?.vm.admins || [];

  // Filter managers based on search term
  const filteredManagers = useMemo(() => {
    if (!searchTerm.trim()) return managers;

    const search = searchTerm.toLowerCase();
    return managers.filter(
      (manager) =>
        manager.profile.preferredName.toLowerCase().includes(search) ||
        manager.profile.lastName.toLowerCase().includes(search) ||
        manager.profile.email.toLowerCase().includes(search)
    );
  }, [managers, searchTerm]);

  // Get selected managers for display
  const selectedManagers = useMemo(() => {
    return managers.filter((manager) => selectedManagerIds.includes(manager.managerId));
  }, [managers, selectedManagerIds]);

  const handleManagerToggle = (managerId: string) => {
    if (disabled) return;

    const isSelected = selectedManagerIds.includes(managerId);
    if (isSelected) {
      onChange(selectedManagerIds.filter((id) => id !== managerId));
    } else {
      onChange([...selectedManagerIds, managerId]);
    }
  };

  const handleManagerRemove = (managerId: string) => {
    if (disabled) return;
    onChange(selectedManagerIds.filter((id) => id !== managerId));
  };

  const getManagerDisplayName = (manager: Manager) => {
    return `${manager.profile.preferredName} ${manager.profile.lastName}`.trim();
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box className={classes.container}>
        <Typography variant='body2' color='error' className={classes.emptyState}>
          Error loading managers: {queryError.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Search Field */}
      <TextField
        fullWidth
        size='small'
        placeholder='Search managers...'
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        variant='outlined'
        className={classes.searchField}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon fontSize='small' />
            </InputAdornment>
          ),
        }}
      />

      {/* Selected Managers */}
      {selectedManagers.length > 0 && (
        <Box className={classes.selectedManagersContainer}>
          {selectedManagers.map((manager) => (
            <Chip
              key={manager.managerId}
              label={getManagerDisplayName(manager)}
              onDelete={disabled ? undefined : () => handleManagerRemove(manager.managerId)}
              avatar={
                <Avatar src={manager.profile.avatarUrl} className={classes.avatar}>
                  {manager.profile.preferredName.charAt(0)}
                </Avatar>
              }
              className={classes.managerChip}
              color='primary'
              variant='outlined'
            />
          ))}
        </Box>
      )}

      {/* Managers List */}
      {filteredManagers.length === 0 ? (
        <Typography variant='body2' className={classes.emptyState}>
          {searchTerm ? 'No managers found matching your search.' : 'No managers available.'}
        </Typography>
      ) : (
        <List className={classes.managersList} dense>
          {filteredManagers.map((manager) => {
            const isSelected = selectedManagerIds.includes(manager.managerId);

            return (
              <ListItem
                key={manager.managerId}
                button
                onClick={() => handleManagerToggle(manager.managerId)}
                className={classes.listItem}
                disabled={disabled}
              >
                <ListItemAvatar>
                  <Avatar src={manager.profile.avatarUrl} className={classes.avatar}>
                    {manager.profile.preferredName.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography className={classes.managerName}>{getManagerDisplayName(manager)}</Typography>}
                  secondary={<Typography className={classes.managerEmail}>{manager.profile.email}</Typography>}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge='end'
                    checked={isSelected}
                    onChange={() => handleManagerToggle(manager.managerId)}
                    disabled={disabled}
                    color='primary'
                  />
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Error Display */}
      {error && (
        <Typography variant='body2' color='error' style={{ marginTop: '8px', fontSize: '12px' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export { ManagerSelector };
